import type * as React from 'react'

import { Flex, Separator } from '@buffer-mono/popcorn'

import { SplitProtected } from '~publish/components/SplitProtected'
import { useLinkShorteningDisabledOverride } from '~publish/hooks/useLinkShorteningDisabledOverride'
import { selectCurrentProfile } from '~publish/legacy/profile-sidebar/selectors'
import ProfilesDisconnectedBanner from '~publish/legacy/profiles-disconnected-banner'
import { useAppSelector } from '~publish/legacy/store'
import getErrorBoundary from '~publish/legacy/web/components/ErrorBoundary'

import {
  BulkPostSetting,
  BULK_UPLOAD_EXCLUDED_SERVICES,
} from './components/BulkPostUpload'
import { GoogleAnalytics } from './components/GoogleAnalytics'
import {
  LinkShortening,
  SHORTENER_DISABLED_SERVICES,
} from './components/LinkShortening'
import { QueueActions } from './components/QueueActions'
import { RemindersSettings } from './components/RemindersSettings'
import { selectGeneralSettings } from './selectors'

const ErrorBoundary = getErrorBoundary(true)

const GeneralSettings = (): React.JSX.Element => {
  const profile = useAppSelector(selectCurrentProfile)
  const generalSettings = useAppSelector(selectGeneralSettings)
  const linkShorteningDisabled = useLinkShorteningDisabledOverride()

  if (!profile) return <></>

  const isManager = profile.isManager

  const showBulkPostUpload =
    isManager && !BULK_UPLOAD_EXCLUDED_SERVICES.includes(profile.service)

  const isInstagramPersonalProfile =
    profile.service === 'instagram' && profile.service_type === 'profile'
  const isRemindersOnly =
    generalSettings.isRemindersOnly || isInstagramPersonalProfile
  const isDisconnectedProfile = profile.isDisconnected
  const supportsMobileReminders =
    profile.service === 'instagram' ||
    profile.service === 'youtube' ||
    profile.service === 'tiktok' ||
    (profile.service === 'facebook' && profile.service_type === 'group')

  const showGoogleAnalytics = !SHORTENER_DISABLED_SERVICES.includes(
    profile.service,
  )

  return (
    <ErrorBoundary>
      {isDisconnectedProfile && <ProfilesDisconnectedBanner />}
      <Flex direction="column" data-testid="general-settings-wrapper">
        {supportsMobileReminders && (
          <RemindersSettings isRemindersOnly={isRemindersOnly} />
        )}

        {!linkShorteningDisabled && (
          <>
            <LinkShortening />
          </>
        )}

        {showGoogleAnalytics && (
          <>
            <Separator />
            <GoogleAnalytics />
          </>
        )}
        <SplitProtected name="bulk-post-upload-csv">
          {showBulkPostUpload && (
            <>
              <Separator />
              <BulkPostSetting />
            </>
          )}
        </SplitProtected>
        {isManager && (
          <>
            <Separator />
            <QueueActions />
          </>
        )}
      </Flex>
    </ErrorBoundary>
  )
}

export { GeneralSettings }
